import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import VueClazyLoad from 'vue-clazy-load';
import intersectionObserver from 'intersection-observer';

import store from './store';
import forms from './forms';
import polyfills from './polyfills';

// Error Monitoring
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';

if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
    Sentry.init({
        dsn: 'https://17580a0b88574d1aaf472544683c1f49@sentry.io/1205840',
        whitelistUrls: [process.env.APP_URL],
        environment: process.env.NODE_ENV,
        ignoreErrors: ['livechatinc.com'],
        integrations: [new Integrations.Vue({
            Vue,
            attachProps: true,
            logErrors: true,
        })],
    });
}

// Directives
import LazyLoadDirective from './vue-directives/lazyload.vue';
import ScrollToDirective from './vue-directives/scrollTo.vue';

// Components
import { ShowAtComponent, HideAtComponent, WithBreakpointComponent } from './apps/breakpoints';
import AccordionItemComponent from './apps/accordion-item.vue';
import AccordionComponent from './apps/accordion.vue';
import AnnouncementsComponent from './apps/announcements.vue';
import CalculatorComponent from './apps/calculator.vue';
import ChartComponent from './apps/chart.vue';
import CommunityAmenitiesComponent from './apps/community-amenities.vue';
import CommunityCardComponent from './apps/community-card.vue';
import ContactAndDirectionsMapComponent from './apps/contact-and-directions-map.vue';
import FloorplanImageGalleryComponent from './apps/floorplan-image-gallery.vue';
import FloorplanDetail from './apps/floorplan-detail.vue';
import Focus360Component from './apps/focus360.vue';
import HomepageCarouselComponent from './apps/homepage-carousel.vue';
import MainNavComponent from './apps/main-nav.vue';
import MediaGalleryComponent from './apps/media-gallery.vue';
import MicrositeComponent from './apps/microsite.vue';
import ModalResidenceGalleryComponent from './apps/modal-residence-gallery.vue';
import NativeSelectComponent from './atoms/native-select.vue';
import OpenModalComponent from './apps/open-modal.vue';
import PaginationComponent from './apps/pagination.vue';
import ParallaxComponent from './apps/parallax.vue';
import PopoverComponent from './apps/popover.vue';
import QuickDeliveryHomesComponent from './apps/quick-delivery-homes.vue';
import SiteplanMapComponent from './apps/siteplan-map.vue';
import SlickComponent from './vendor/slick.vue';
import SrcSet from './apps/src-set.vue';
import TestimonialsComponent from './apps/testimonials.vue';
import ToggleButtonsComponent from './apps/toggle-buttons.vue';
import TruncateComponent from './apps/truncate.vue';
import VideoModalComponent from './apps/video-modal.vue';
import WatchScrollComponent from './apps/watch-scroll.vue';
import WhereWeBuildComponent from './apps/where-we-build.vue';
import WhereWeBuildMapComponent from './apps/where-we-build-map.vue';

// Compose Directives
Vue.directive('scroll-to', ScrollToDirective);
Vue.directive('lazy-load', LazyLoadDirective);

// Compose components
Vue.component('accordion-item', AccordionItemComponent);
Vue.component('accordion', AccordionComponent);
Vue.component('announcements', AnnouncementsComponent); 
Vue.component('calculator', CalculatorComponent);
Vue.component('community-amenities', CommunityAmenitiesComponent);
Vue.component('community-card', CommunityCardComponent);
Vue.component('community-nav', MicrositeComponent);
Vue.component('contact-and-directions-map', ContactAndDirectionsMapComponent);
Vue.component('doughnut-chart', ChartComponent);
Vue.component('floorplan-detail', FloorplanDetail);
Vue.component('floorplan-image-gallery', FloorplanImageGalleryComponent);
Vue.component('focus360', Focus360Component);
Vue.component('hide-at', HideAtComponent);
Vue.component('homepage-carousel', HomepageCarouselComponent);
Vue.component('main-nav', MainNavComponent);
Vue.component('media-gallery', MediaGalleryComponent);
Vue.component('modal-residence-gallery', ModalResidenceGalleryComponent);
Vue.component('quick-delivery-homes', QuickDeliveryHomesComponent);
Vue.component('native-select', NativeSelectComponent);
Vue.component('open-modal', OpenModalComponent);
Vue.component('pagination', PaginationComponent);
Vue.component('parallax', ParallaxComponent);
Vue.component('popover', PopoverComponent);
Vue.component('quick-delivery-homes', QuickDeliveryHomesComponent);
Vue.component('show-at', ShowAtComponent);
Vue.component('siteplan-map', SiteplanMapComponent);
Vue.component('slick', SlickComponent);
Vue.component('src-set', SrcSet);
Vue.component('testimonials', TestimonialsComponent);
Vue.component('toggle-buttons', ToggleButtonsComponent);
Vue.component('truncate', TruncateComponent);
Vue.component('video-modal', VideoModalComponent);
Vue.component('watch-scroll', WatchScrollComponent);
Vue.component('where-we-build', WhereWeBuildComponent);
Vue.component('where-we-build-map', WhereWeBuildMapComponent);
Vue.component('with-breakpoint', WithBreakpointComponent);

Vue.use(VueClazyLoad);

const app = new Vue({
    el: '#app',
    store: store,
    delimiters: ['<%', '%>'],
    mixins: [WithBreakpointComponent],
    data: {
        mainNav: false,
        modalOpen: false,
        videoModalOpen: false,
        subNav: false,
        active: false,
        modalContent: {},
        isActive: false,
        opened: false,
        footerChat: false,
        sidebarSignup: false,
        MSHMobile: false,
        lastActiveNavItem: '',
        subNavItem: {
            search: false,
            buying: false,
            about: false,
            talk: false
        },
        baseUrl: `${window.location.origin}${window.location.pathname}`
    },
    computed: {
        ...mapGetters({
            appData: 'app/getData',
            modalData: 'modal/getData'
        })
    },
    watch: {
        subNavItem: {
            deep: true,
            handler(val) {
                if (val) {
                    document.addEventListener('keydown', this.handleMenuKeyDown, false);
                } else {
                    document.removeEventListener('keydown', this.handleMenuKeyDown);
                }
            }
        }
    },
    created() {
        // Remove .no-js from html - javascript is not disabled
        if (document.querySelector('no-js')) {
            document.querySelector('.no-js').classList.remove('no-js');
        }

        // bind the application-level data to the store
        if (typeof JsonData !== 'undefined') {
            this.fillAppData(JsonData);
        }

        window.addEventListener('load', this.handleWindowLoad, false);
        this.$eventsBus.$on('open-modal', this.handleOpenModal);
        this.$eventsBus.$on('open-video-modal', this.handleOpenVideoModal);
        this.$eventsBus.$on('close-modal', this.handleCloseModal);
        this.$eventsBus.$on('breakpoint-change', this.handleBreakpointChange);

        // modal ada stuff
        this.focusableElems = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        this.focusableContent = null;
        this.firstFocusableElem = null;
        this.lastFocusableElem = null;
        this.isListeningForKeyDown = false;
    },
    beforeDestroy() {
        this.$eventsBus.$off('open-modal', this.handleOpenModal);
        this.$eventsBus.$off('open-video-modal', this.handleOpenVideoModal);
        this.$eventsBus.$off('close-modal', this.handleCloseModal);
        this.$eventsBus.$off('breakpoint-change', this.handleBreakpointChange);
        window.removeEventListener('keydown', this.handleMenuKeyDown);
    },
    methods: {
        handleBreakpointChange() {
            if (this.currentBreakpoint !== 'phone') {
                this.subNav = false;
                Object.keys(this.subNavItem).forEach((key) => {
                    this.subNavItem[key] = false;
                });
            }
        },
        handleOpenModal(params) {
            this.openModal(params[0], params[1]);
        },
        handleOpenVideoModal(params) {
            this.openVideoModal(params[0], params[1]);
        },
        handleCloseModal() {
            this.closeModal();
        },
        handleWindowLoad() {
            this.$eventsBus.$emit('ready');
            window.removeEventListener('load', this.handleWindowLoad);
        },
        openModal(modal, modalData) {
            this.modalOpen = modal;
            if (modal !== 'modal-contact-us') {
                this.setModalData(modalData);
                this.setActiveModal(modal);
            }
            this.$nextTick(this.handleModalAda)
        },
        openVideoModal(modal, modalData) {
            this.videoModalOpen = modal;
            this.setModalData(modalData);
            this.setActiveModal(modal);
        },
        closeModal() {
            const iframe = document.querySelector('.modal-video iframe');
            this.setActiveModal(this.modalOpen = false);
            this.setActiveModal(this.videoModalOpen = false);
            if (iframe) {
                const iframeSrc = iframe.src;
                iframe.src = iframeSrc;
            }
            document.removeEventListener('keydown', this.handleModalKeyDown, false);
            this.isListeningForKeyDown = false;
            this.focusableContent = null;
            this.firstFocusableElem = null;
            this.lastFocusableElem = null;
        },
        isModalOpen(modal) {
            return this.modalOpen === modal;
        },
        handleModalAda() {
            const modalElem = document.querySelector(`.${this.modalOpen}`);
            this.captureFocusables(modalElem);
        },
        handleModalKeyDown(e) {
			const isEscapePressed = e.key === 'Escape' || e.keyCode === 27;
			const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

			if (isEscapePressed) {
                this.closeModal();
			}

			if (!isTabPressed) {
				return;
			}

			if (e.shiftKey) {
				if (document.activeElement === this.firstFocusableElem) {
					this.lastFocusableElem.focus();
					e.preventDefault();
				}
			} else {
				if (document.activeElement === this.lastFocusableElem) {
					this.firstFocusableElem.focus();
					e.preventDefault();
				}
			}
		},
        captureFocusables(modal) {
			this.focusableContent = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            console.log(this.focusableContent);
			this.firstFocusableElem = this.focusableContent[0];
			this.lastFocusableElem = this.focusableContent[this.focusableContent.length - 1];

			if (!this.isListeningForKeyDown) {
				this.isListeningForKeyDown = true;
				document.addEventListener('keydown', this.handleModalKeyDown, false);
			}

			this.firstFocusableElem.focus();
		},
        setModalElem(modal) {
            const modalElem = document.querySelector(`.${modal}`);
            this.captureFocusables(modalElem);
        },
        toggleSubNav(menu) {
            console.log('oh no:');
            this[menu] = !this[menu];
            Object.keys(this.subNavItem).forEach((key) => {
                this.subNavItem[key] = false;
            });
        },
        toggleSubNavItem(menu) {
            let activeKey = '';
            Object.keys(this.subNavItem).forEach((key) => {
                if (key === menu) {
                    activeKey = !this.subNavItem[key] ? menu : '';
                    this.subNavItem[key] = !this.subNavItem[key];
                } else {
                    this.subNavItem[key] = false;
                }
            });
            this.lastActiveNavItem = activeKey;
        },
        handleNavItemKeyed(ev, menu) {
            if (ev.key === 'Enter' || ev.key === ' ') {
                if (menu === 'subNav') {
                    this.toggleSubNav(menu);
                } else {
                    this.toggleSubNavItem(menu);
                }
            }
        },
        handleMenuKeyDown(ev) {
            if (ev.key === 'Escape') {
                const lastOpen = this.lastActiveNavItem
                    ? document.querySelector(`#mainNav${this.lastActiveNavItem[0].toUpperCase() + this.lastActiveNavItem.slice(1, this.lastActiveNavItem.length)}`)
                    : null;
                this.toggleSubNavItem('');
                this.subNav = false;
                Object.keys(this.subNavItem).forEach((key) => {
                    this.subNavItem[key] = false;
                });
                if (lastOpen) {
                    lastOpen.focus();
                }
                console.log(lastOpen);
            }
        },
        scrollIntoView(selector, isExact) {
            const el = document.querySelector(selector);
            if (el) {
                if (isExact) {
                    const headerHeight = (this.currentBreakpoint === 'phone')
                        ? 80 // Mobile header height
                        : (this.currentBreakpoint === 'tablet')
                            ? 100 // Tablet header height
                            : 126; // Greater than tablet header height
                    const offset = $(el).offset().top - headerHeight;
                    $('html,body').animate({scrollTop: offset}, 600, 'swing');
                } else {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'nearest'
                    });
                }
            }
        },
        ...mapActions({
            fillAppData: 'app/fill',
            addAppAttribute: 'app/put',
            setModalData: 'modal/setData',
            mergeModalData: 'modal/merge',
            setActiveModal: 'modal/setActive'
        })
    }
});
